/**
 * Phase VV.65 — Backup auto-refresh via /api/version polling.
 *
 * The service-worker `updatefound` path (see `registerSW.js`) is our
 * primary auto-refresh signal, but it's flaky on the Capacitor Android
 * WebView and older iOS Safari. This module gives us a second, dumb-
 * reliable path:
 *
 *   1. On boot, GET /api/version → stash `build_id` in memory.
 *   2. Every 60 s, GET /api/version again.
 *   3. If `build_id` differs, show a toast and hard-reload the page.
 *
 * Idempotent + no dependencies on the service worker.
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 60 * 1000;

let _started = false;
let _lastBuildId = null;

async function _fetchBuildId() {
  try {
    const r = await fetch(`${BACKEND_URL}/api/version`, {
      cache: "no-store",
      credentials: "omit",
    });
    if (!r.ok) return null;
    const j = await r.json();
    return j && j.build_id ? String(j.build_id) : null;
  } catch (_) {
    return null;
  }
}

async function _reload() {
  // Toast (best-effort) then hard-reload. Guarded so we only fire once
  // per session even if a race triggers two poll ticks close together.
  const KEY = "pys.version.reloading";
  try {
    if (sessionStorage.getItem(KEY) === "1") return;
    sessionStorage.setItem(KEY, "1");
  } catch (_) {
    /* private mode — proceed */
  }
  try {
    const { toast } = await import("sonner");
    toast("Fresh PYS available — reloading…", { duration: 2500 });
  } catch (_) {
    /* toast unavailable */
  }
  setTimeout(() => window.location.reload(), 600);
}

export function startVersionPoller() {
  if (_started || typeof window === "undefined") return;
  if (!BACKEND_URL) return;
  _started = true;
  (async () => {
    _lastBuildId = await _fetchBuildId();
    setInterval(async () => {
      // Skip polling when the tab is hidden — the browser's own
      // resource cache will pick up the new bundle on next focus,
      // and no user is watching a reloading toast anyway.
      if (document.visibilityState === "hidden") return;
      const bid = await _fetchBuildId();
      if (!bid || !_lastBuildId) {
        _lastBuildId = bid || _lastBuildId;
        return;
      }
      if (bid !== _lastBuildId) {
        _lastBuildId = bid;
        _reload();
      }
    }, POLL_INTERVAL_MS);
  })();
}
